import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import type { SparePart } from '@/types'
import { motion } from 'framer-motion'
import { ShoppingCart, Eye } from 'lucide-react'
import { formatPrice, normalizeCurrency, type CurrencyMode } from '@/lib/currency'

interface PartCardProps {
  part: SparePart
  onView: (part: SparePart) => void
  onAddToCart: (part: SparePart, quantity: number) => void
  /** `dense` = tarjeta compacta para cuadrícula de 2 columnas. */
  dense?: boolean
  /** Modo de moneda elegido por el usuario; el precio se guarda en `part.currency`. */
  displayCurrency?: CurrencyMode
}

export function PartCard({
  part,
  onView,
  onAddToCart,
  dense = false,
  displayCurrency = 'ORIGINAL',
}: PartCardProps) {
  const image = part.images?.[0]
  const price = part.price ?? 0
  const source = normalizeCurrency(part.currency)
  const priceLabel = formatPrice(price, source, displayCurrency)

  // ─── Variante densa (cuadrícula 2 columnas) ───
  if (dense) {
    return (
      <Card
        className="cursor-pointer overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.07)] transition-all duration-300 hover:border-[#E10F18]/35 hover:shadow-[0_12px_28px_rgba(15,23,42,0.14)]"
        onClick={() => onView(part)}
      >
        <CardContent className="p-0">
          <div className="relative aspect-[5/4] overflow-hidden bg-muted">
            {image ? (
              <img src={image} alt={part.name} loading="lazy" className="h-full w-full object-contain" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-muted-foreground">
                Sin imagen
              </div>
            )}
            {part.featured && (
              <span className="absolute left-2 top-2 rounded bg-[#E10F18] px-1.5 py-0.5 text-[10px] font-medium text-white">
                Destacado
              </span>
            )}
          </div>
          <div className="space-y-1 p-2.5">
            <h3 className="line-clamp-2 text-sm font-semibold leading-tight text-gray-900">{part.name}</h3>
            <p className="truncate text-xs text-gray-500">{part.brand?.name}</p>
            <div className="flex items-center justify-between gap-1 pt-1">
              <span className="text-sm font-bold text-[#E10F18]">{priceLabel}</span>
              <button
                type="button"
                aria-label={`Añadir ${part.name} al carrito`}
                onClick={(e) => {
                  e.stopPropagation()
                  onAddToCart(part, 1)
                }}
                className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-[#E10F18] text-white transition-colors hover:bg-[#c00d15]"
              >
                <ShoppingCart className="h-4 w-4" />
              </button>
            </div>
          </div>
        </CardContent>
      </Card>
    )
  }

  // ─── Variante por defecto ───
  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      whileHover={{ scale: 1.02 }}
      transition={{ duration: 0.2 }}
      className="cursor-pointer"
    >
      <Card
        className="overflow-hidden transition-all duration-300 group bg-white rounded-2xl border border-gray-200/80 hover:border-[#E10F18]/35 shadow-[0_12px_30px_rgba(15,23,42,0.08)] hover:shadow-[0_18px_40px_rgba(15,23,42,0.16)]"
        onClick={() => onView(part)}
      >
        <CardContent className="p-0">
          <div className="relative aspect-[5/4] overflow-hidden bg-muted">
            {image ? (
              <img
                src={image}
                alt={part.name}
                className="w-full h-full object-contain transition-transform duration-300 group-hover:scale-105"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-muted-foreground text-sm">
                Sin imagen
              </div>
            )}
            {part.featured && (
              <span className="absolute top-2 left-2 rounded bg-[#E10F18] px-2 py-0.5 text-xs font-medium text-white">
                Destacado
              </span>
            )}
          </div>
          <div className="p-4 space-y-2">
            <h3 className="font-semibold text-gray-900 line-clamp-2">{part.name}</h3>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              {part.brand?.name && <span>{part.brand.name}</span>}
              {part.category?.name && (
                <>
                  <span>·</span>
                  <span>{part.category.name}</span>
                </>
              )}
            </div>
            <p className="text-lg font-bold text-[#E10F18]">{priceLabel}</p>
            <div className="flex gap-2 pt-2" onClick={(e) => e.stopPropagation()}>
              <Button
                variant="outline"
                size="sm"
                className="flex-1 gap-1"
                onClick={(e) => {
                  e.stopPropagation()
                  onView(part)
                }}
              >
                <Eye className="h-3.5 w-3.5" />
                Ver detalle
              </Button>
              <Button
                size="sm"
                className="flex-1 gap-1 bg-[#E10F18] hover:bg-[#c00d15]"
                onClick={(e) => {
                  e.stopPropagation()
                  onAddToCart(part, 1)
                }}
              >
                <ShoppingCart className="h-3.5 w-3.5" />
                Añadir
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </motion.div>
  )
}
